import type { Node, Query, Tree } from 'web-tree-sitter';
import type { CodeSymbol, SymbolKind } from './types';
import { CAPTURE_KIND } from './queries';

interface RawDef {
  node: Node;
  kind: SymbolKind;
  name: string;
}

/**
 * Turn a parsed tree + definitions query into a nested outline.
 *
 * Hierarchy rule: a definition's parent is its NEAREST captured ancestor in the
 * AST. We walk `node.parent` upward until we hit another captured definition (or
 * the root). This makes nesting follow real lexical containment: two top-level
 * functions are siblings; a method's nearest captured ancestor is its class; a
 * function nested inside a function nests under it. No naming or indentation
 * heuristics — the AST structure does the work.
 *
 * Pure (no DOM); the caller owns the parser/tree lifecycle.
 */
export function extractSymbolsFromTree(tree: Tree, query: Query): CodeSymbol[] {
  // 1) One match per definition pattern. Each match carries a `@def.*` node
  //    (the kind) and a `@name` node (the identifier text).
  const byId = new Map<number, RawDef>();
  const raws: RawDef[] = [];
  for (const match of query.matches(tree.rootNode)) {
    let defNode: Node | null = null;
    let kind: SymbolKind | null = null;
    let name = '';
    for (const cap of match.captures) {
      if (cap.name === 'name') {
        name = cap.node.text;
      } else {
        const k = CAPTURE_KIND[cap.name];
        if (k) {
          defNode = cap.node;
          kind = k;
        }
      }
    }
    if (!defNode || !kind) continue;
    if (byId.has(defNode.id)) continue; // overlapping patterns -> keep first
    const raw: RawDef = { node: defNode, kind, name: name || '(anonymous)' };
    byId.set(defNode.id, raw);
    raws.push(raw);
  }

  // 2) Build a CodeSymbol shell per definition, then wire children by walking
  //    each node up to its nearest captured ancestor.
  const symbolOf = new Map<number, CodeSymbol>();
  for (const raw of raws) {
    symbolOf.set(raw.node.id, {
      kind: raw.kind,
      name: raw.name,
      line: raw.node.startPosition.row + 1,
      endLine: raw.node.endPosition.row + 1,
      children: [],
    });
  }
  const roots: CodeSymbol[] = [];
  for (const raw of raws) {
    const sym = symbolOf.get(raw.node.id)!;
    let ancestor: Node | null = raw.node.parent;
    while (ancestor && !byId.has(ancestor.id)) ancestor = ancestor.parent;
    if (ancestor) symbolOf.get(ancestor.id)!.children.push(sym);
    else roots.push(sym);
  }

  // 3) Sort each sibling list into source order (query match order is not
  //    guaranteed to be document order).
  const sortByLine = (list: CodeSymbol[]): void => {
    list.sort((a, b) => a.line - b.line || a.endLine - b.endLine);
    for (const s of list) sortByLine(s.children);
  };
  sortByLine(roots);
  return roots;
}
